/**
 * @fileoverview Secured Creditor Notification Animation
 *
 * Purpose: A stylised, looping recreation of Morgan turning a PPSR search
 * result into creditor communications, for the homepage feature card
 * ("Reports, Day Ones, & creditor communications, drafted in seconds").
 *
 * It tells a five-beat story:
 *
 *  1. Document   - a stylised PPSR grantor search result lands as a document
 *                  (abstract "redacted" content — deliberately not real UI).
 *  2. Analysing  - the agent reads the search result, extracts the registered
 *                  security interests, and matches the secured parties. Steps
 *                  tick pending → processing → complete under the violet
 *                  "live agent" header.
 *  3. Populating - the matched secured creditors drop into the "Job Contacts"
 *                  table, each tagged as a Secured Creditor (mirrors the CRM).
 *  4. Prompt     - a card asks the practitioner to "Generate Initial
 *                  Notification to Secured Creditors". A cursor clicks Generate.
 *  5. Letter     - a standalone letter renders with its fields auto-populated
 *                  and highlighted as they fill in. The "Giving of Notice
 *                  Identifier(s)" + "PPSR Registration No." fields make it
 *                  obviously a notice to secured creditors.
 *
 * This is a marketing stylisation — no live data, intentionally abstract so it
 * doesn't give the real UI/UX away — sized to the feature-card container. It
 * reuses Morgan's colour language (violet agent stream, amber secured-party
 * accents, emerald success, primary-green letter) so it reads as Morgan.
 *
 * Respects prefers-reduced-motion by rendering the final "letter" state.
 *
 * Used by: FeatureCardsSection (-sections.feature-cards.tsx)
 */

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Check,
  Contact,
  FileSearch,
  FileText,
  Layers,
  Loader2,
  MousePointer2,
  Send,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/utils/misc";

interface AnalysisTask {
  id: string;
  label: string;
  icon: LucideIcon;
}

/** The steps that tick through while the agent reads the PPSR result. */
const ANALYSIS_TASKS: AnalysisTask[] = [
  { id: "read", label: "Reading PPSR grantor search result", icon: FileSearch },
  {
    id: "extract",
    label: "Extracting registered security interests",
    icon: Layers,
  },
  {
    id: "match",
    label: "Identifying & matching secured parties",
    icon: ShieldCheck,
  },
] as const;

interface SecuredParty {
  id: string;
  name: string;
  /** Stylised PPSR registration number. */
  reg: string;
}

/** Stylised secured parties that populate from the search result. */
const SECURED_PARTIES: SecuredParty[] = [
  { id: "p1", name: "Meridian Asset Finance Pty Ltd", reg: "PPSR 2019•••421" },
  { id: "p2", name: "Capital Equipment Finance", reg: "PPSR 2021•••088" },
  {
    id: "p3",
    name: "Northgate Commercial Finance Pty Ltd",
    reg: "PPSR 2020•••736",
  },
] as const;

/** The recipient the rendered letter is addressed to (first secured party). */
const LETTER_RECIPIENT = SECURED_PARTIES[0];

/** Auto-populated letter fields (stylised) used in the standalone letter beat. */
const LETTER_FIELDS = {
  date: "14 Oct 2025",
  /** "Giving of Notice Identifier(s)" — the secured party's PPSR identifier. */
  identifier: "615226045",
  /** "PPSR Registration No." — the registration number on the search result. */
  ppsrRegistration: "202308070013843",
} as const;

/** The subject company of the job — shown atop Job Contacts and in the letter. */
const COMPANY = {
  name: "Brightwater Logistics Pty Ltd",
  status: "In Liquidation",
  acn: "123 456 789",
  abn: "12 123 456 789",
} as const;

// --- Timing (module scope so the progress line stays in sync with the run) ---
/** How long the PPSR document sits alone before the agent picks it up. */
const DOC_MS = 1500;
/** Per analysis-task processing time. */
const TASK_MS = 720;
/** Stagger between secured-party rows dropping in. */
const PARTY_STAGGER_MS = 380;

const ANALYSIS_END_MS = DOC_MS + ANALYSIS_TASKS.length * TASK_MS + 250;
const POPULATE_END_MS =
  ANALYSIS_END_MS + 250 + SECURED_PARTIES.length * PARTY_STAGGER_MS + 500;
/** Cursor travel + click on the Send button. */
const SEND_CLICK_MS = 1500;
const SENDING_MS = 380;
/** When the letter view takes over. */
const LETTER_START_MS = POPULATE_END_MS + SEND_CLICK_MS + SENDING_MS;
/** How long the finished letter is held before the cycle repeats. */
const LETTER_HOLD_MS = 4600;
/** Total length of one play-through — drives the progress line + orchestrator. */
export const SECURED_CREDITOR_ANIMATION_CYCLE_MS =
  LETTER_START_MS + LETTER_HOLD_MS;

type TaskStatus = "pending" | "processing" | "complete";
type Phase =
  "document" | "analysing" | "populating" | "prompt" | "sending" | "letter";

interface SecuredCreditorAnimationProps {
  /**
   * When false, the animation rests on the finished "letter" view (no
   * playback, no progress line). The parent orchestrator toggles this so only
   * one card animates at a time.
   */
  isActive?: boolean;
}

export function SecuredCreditorAnimation({
  isActive = true,
}: SecuredCreditorAnimationProps) {
  const reducedMotion = useReducedMotion() ?? false;

  const [phase, setPhase] = useState<Phase>("document");
  const [taskStatuses, setTaskStatuses] = useState<TaskStatus[]>(
    ANALYSIS_TASKS.map(() => "pending"),
  );
  const [visibleParties, setVisibleParties] = useState(0);
  // Cursor position (x %, y px within the body) for the Send gesture.
  const [cursor, setCursor] = useState({ x: 62, y: 70, visible: false });
  // Bumped at the start of every play-through so the progress line restarts.
  const [cycle, setCycle] = useState(0);

  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  useEffect(() => {
    // Resting / static state: the finished letter.
    if (reducedMotion || !isActive) {
      timers.current.forEach(clearTimeout);
      timers.current = [];
      setPhase("letter");
      setTaskStatuses(ANALYSIS_TASKS.map(() => "complete"));
      setVisibleParties(SECURED_PARTIES.length);
      setCursor((c) => ({ ...c, visible: false }));
      return;
    }

    const clearAll = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };

    const after = (ms: number, fn: () => void) => {
      timers.current.push(setTimeout(fn, ms));
    };

    const setTask = (index: number, status: TaskStatus) =>
      setTaskStatuses((prev) => {
        const next = [...prev];
        next[index] = status;
        return next;
      });

    const runCycle = () => {
      // Reset
      setPhase("document");
      setCycle((c) => c + 1);
      setTaskStatuses(ANALYSIS_TASKS.map(() => "pending"));
      setVisibleParties(0);
      setCursor({ x: 62, y: 70, visible: false });

      // Beat 2: agent analyses the document.
      after(DOC_MS, () => setPhase("analysing"));
      ANALYSIS_TASKS.forEach((_, i) => {
        after(DOC_MS + i * TASK_MS, () => setTask(i, "processing"));
        after(DOC_MS + i * TASK_MS + TASK_MS - 160, () =>
          setTask(i, "complete"),
        );
      });

      // Beat 3: secured creditors populate.
      after(ANALYSIS_END_MS, () => setPhase("populating"));
      SECURED_PARTIES.forEach((_, i) => {
        after(ANALYSIS_END_MS + 250 + i * PARTY_STAGGER_MS, () =>
          setVisibleParties(i + 1),
        );
      });

      // Beat 4: prompt to send the initial notification; cursor clicks Send.
      after(POPULATE_END_MS, () => {
        setPhase("prompt");
        setCursor({ x: 70, y: 70, visible: true });
      });
      // Travel to and rest on the Generate button (bottom-right of the prompt).
      after(POPULATE_END_MS + 750, () =>
        setCursor({ x: 66, y: 182, visible: true }),
      );
      after(POPULATE_END_MS + SEND_CLICK_MS, () => setPhase("sending"));

      // Beat 5: the letter renders with auto-populated fields.
      after(LETTER_START_MS, () => {
        setPhase("letter");
        setCursor((c) => ({ ...c, visible: false }));
      });

      // Hold on the finished letter, then loop.
      after(SECURED_CREDITOR_ANIMATION_CYCLE_MS, runCycle);
    };

    runCycle();
    return clearAll;
  }, [reducedMotion, isActive]);

  const playing = isActive && !reducedMotion;
  const analysing = phase === "analysing";
  const showLetter = phase === "letter";
  const showPrompt = phase === "prompt" || phase === "sending";
  // The list of secured parties underlies every beat after the document.
  const showList =
    phase === "populating" || phase === "prompt" || phase === "sending";
  const partyCountKnown =
    phase !== "document" && phase !== "analysing" ? SECURED_PARTIES.length : 0;

  return (
    <div className="absolute inset-0 overflow-hidden rounded-lg bg-white dark:bg-card">
      {/* Progress line — fills along the bottom while this animation plays */}
      {playing && (
        <motion.div
          key={cycle}
          className="absolute inset-x-0 bottom-0 z-50 h-[3px] bg-primary"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{
            duration: SECURED_CREDITOR_ANIMATION_CYCLE_MS / 1000,
            ease: "linear",
          }}
          style={{ originX: 0 }}
        />
      )}

      <div className="flex h-full w-full flex-col">
        {/* Title bar */}
        <div className="flex items-center gap-2 border-b border-primary/10 bg-primary/[0.03] px-3 py-2">
          <div className="flex gap-1.5">
            <span className="h-2 w-2 rounded-full bg-red-400/70" />
            <span className="h-2 w-2 rounded-full bg-amber-400/70" />
            <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
          </div>
          <span className="ml-1 text-[11px] font-semibold text-primary">
            Security Interests
          </span>
          {partyCountKnown > 0 && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="ml-auto flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
            >
              <ShieldCheck className="h-2.5 w-2.5" />
              {partyCountKnown}
            </motion.span>
          )}
        </div>

        {/* Body */}
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-3">
          {/* Agent panel (mirrors ManagedAgentLivePanel) — shown while reading */}
          {(phase === "document" || phase === "analysing") && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="shrink-0 rounded-md border border-violet-200 bg-violet-50/60 dark:border-violet-500/25 dark:bg-violet-500/10"
            >
              <div className="flex items-center gap-1.5 border-b border-violet-200/70 px-2.5 py-1.5 dark:border-violet-500/20">
                <span className="text-[10px] font-semibold text-violet-900 dark:text-violet-200">
                  Analysing Security Interests
                </span>
                {analysing ? (
                  <Loader2 className="h-3 w-3 animate-spin text-violet-500" />
                ) : (
                  <Sparkles className="h-3 w-3 text-violet-400" />
                )}
              </div>

              {analysing ? (
                <div className="space-y-1 px-2.5 py-2">
                  {ANALYSIS_TASKS.map((task, i) => {
                    const status = taskStatuses[i];
                    const Icon = task.icon;
                    return (
                      <div
                        key={task.id}
                        className={cn(
                          "flex items-center gap-1.5 text-[10px] leading-snug transition-opacity",
                          status === "pending"
                            ? "text-violet-700/35 dark:text-violet-300/30"
                            : "text-violet-900 dark:text-violet-200",
                        )}
                      >
                        <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                          {status === "complete" ? (
                            <Check
                              className="h-3 w-3 text-emerald-600"
                              strokeWidth={3}
                            />
                          ) : status === "processing" ? (
                            <Loader2 className="h-3 w-3 animate-spin text-violet-500" />
                          ) : (
                            <Icon className="h-3 w-3 text-violet-400/60" />
                          )}
                        </span>
                        <span className="truncate">{task.label}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-1.5 px-2.5 py-2 text-[10px] font-medium text-violet-900/70 dark:text-violet-200/70">
                  <Loader2 className="h-3 w-3 animate-spin text-violet-500" />
                  <span className="truncate">
                    Search result received — preparing analysis…
                  </span>
                </div>
              )}
            </motion.div>
          )}

          {/* Beat 1 + 2: stylised PPSR search-result document */}
          {(phase === "document" || phase === "analysing") && (
            <div className="mt-2.5 flex min-h-0 flex-1 items-center justify-center">
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="relative w-full max-w-[260px]"
              >
                {/* Scan sweep while analysing */}
                {analysing && (
                  <motion.div
                    className="absolute inset-x-0 z-10 h-8 rounded bg-gradient-to-b from-violet-400/0 via-violet-400/25 to-violet-400/0"
                    initial={{ top: "0%" }}
                    animate={{ top: ["0%", "84%", "0%"] }}
                    transition={{
                      duration: 1.8,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                )}
                <div className="overflow-hidden rounded-md border border-primary/15 bg-white shadow-sm dark:bg-card">
                  {/* Doc header */}
                  <div className="flex items-center gap-1.5 border-b border-primary/10 bg-primary/[0.04] px-2.5 py-1.5">
                    <FileText className="h-3.5 w-3.5 text-primary" />
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-primary">
                      PPSR
                    </span>
                    <span className="ml-auto text-[8px] font-medium text-primary/50">
                      Grantor Search Result
                    </span>
                  </div>
                  {/* Doc body — abstract redacted content + registration rows */}
                  <div className="space-y-2 px-3 py-2.5">
                    <div className="space-y-1">
                      <div className="h-1.5 w-2/3 rounded-full bg-primary/15" />
                      <div className="h-1.5 w-1/2 rounded-full bg-primary/10" />
                    </div>
                    {[0, 1, 2].map((row) => (
                      <div
                        key={row}
                        className="flex items-center gap-1.5 rounded border border-primary/10 bg-primary/[0.02] px-1.5 py-1"
                      >
                        <span className="h-5 w-1 shrink-0 rounded-full bg-amber-400/70" />
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="h-1.5 w-3/4 rounded-full bg-primary/20" />
                          <div className="h-1.5 w-1/2 rounded-full bg-primary/10" />
                        </div>
                        <ShieldCheck className="h-3 w-3 shrink-0 text-amber-500/70" />
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Beat 3 + 4: job contacts populate (underlies the prompt) */}
          {showList && (
            <div className="flex min-h-0 flex-1 flex-col pt-0.5">
              {/* Table header — Job Contacts (the CRM) with the job name right-aligned */}
              <div className="mb-2 flex items-center gap-2 px-0.5">
                <Contact className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="text-[10.5px] font-semibold text-primary">
                  Job Contacts
                </span>
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[8.5px] font-semibold text-amber-700">
                  Secured Creditors
                </span>
                <p className="ml-auto truncate text-[10.5px] font-semibold text-foreground">
                  {COMPANY.name}{" "}
                  <span className="font-normal text-foreground/50">
                    ({COMPANY.status})
                  </span>
                </p>
              </div>

              <div className="relative min-h-0 flex-1 divide-y divide-primary/5 overflow-hidden">
                {SECURED_PARTIES.map((party, i) => {
                  const isVisible = i < visibleParties;
                  return (
                    <motion.div
                      key={party.id}
                      initial={false}
                      animate={
                        isVisible
                          ? { opacity: 1, height: "auto" }
                          : { opacity: 0, height: 0 }
                      }
                      transition={{ duration: 0.3 }}
                      className={cn(
                        "flex items-center gap-2 px-0.5 py-1.5",
                        isVisible ? "overflow-visible" : "overflow-hidden",
                      )}
                    >
                      {/* Avatar */}
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                        <ShieldCheck className="h-3 w-3" />
                      </span>
                      {/* Name + reg */}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[10px] font-medium leading-tight text-foreground">
                          {party.name}
                        </p>
                        <p className="truncate font-mono text-[8.5px] leading-tight text-primary/50">
                          {party.reg}
                        </p>
                      </div>
                      {/* Role pill */}
                      <span className="inline-flex shrink-0 items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[8px] font-semibold text-amber-700">
                        <ShieldCheck className="h-2.5 w-2.5" />
                        Secured Creditor
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Beat 4: prompt to send the initial notification */}
          {showPrompt && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 p-4 backdrop-blur-[1px]"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="w-full max-w-[280px] overflow-hidden rounded-lg border border-primary/15 bg-white shadow-xl dark:bg-card"
              >
                <div className="flex items-start gap-2.5 px-3.5 pt-3.5">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Send className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold leading-snug text-foreground">
                      Generate Initial Notification to Secured Creditors?
                    </p>
                    <p className="mt-0.5 text-[9px] leading-snug text-foreground/55">
                      {SECURED_PARTIES.length} secured creditors identified from
                      the PPSR search result.
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-1.5 px-3.5 py-3">
                  <span className="rounded-md border border-primary/15 px-2.5 py-1 text-[9px] font-medium text-primary/55">
                    Later
                  </span>
                  <motion.span
                    animate={
                      phase === "sending"
                        ? { scale: [1, 0.9, 1] }
                        : { scale: 1 }
                    }
                    transition={{ duration: 0.3 }}
                    className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-[9px] font-semibold text-primary-foreground"
                  >
                    <Send className="h-2.5 w-2.5" /> Generate
                  </motion.span>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Beat 5: standalone letter with auto-populated fields */}
          {showLetter && (
            <>
              {/* Auto-fill chip floats over the document canvas */}
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="absolute right-1 top-1 z-30 inline-flex items-center gap-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[8px] font-semibold text-emerald-700"
              >
                <Sparkles className="h-2.5 w-2.5" /> Fields auto-filled
              </motion.span>

              {/* Document canvas — the letter stands alone (no app chrome) */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="flex min-h-0 flex-1 items-center justify-center rounded-md bg-primary/[0.04]"
              >
                <div className="relative w-full max-w-[310px]">
                  {/* Two more letters generated at once, stacked behind (one
                      per remaining secured creditor) — they fan out as the
                      batch is generated. */}
                  <motion.div
                    aria-hidden="true"
                    initial={{ opacity: 0, x: 0, y: 0 }}
                    animate={{ opacity: 1, x: 13, y: 13 }}
                    transition={{ delay: 0.18, duration: 0.4, ease: "easeOut" }}
                    className="absolute inset-0 rounded-sm bg-white shadow-[0_2px_10px_rgba(0,0,0,0.08)] ring-1 ring-primary/[0.06] dark:bg-card"
                  />
                  <motion.div
                    aria-hidden="true"
                    initial={{ opacity: 0, x: 0, y: 0 }}
                    animate={{ opacity: 1, x: 6, y: 6 }}
                    transition={{ delay: 0.1, duration: 0.4, ease: "easeOut" }}
                    className="absolute inset-0 rounded-sm bg-white shadow-[0_2px_10px_rgba(0,0,0,0.08)] ring-1 ring-primary/[0.06] dark:bg-card"
                  />
                  {/* The letter on top */}
                  <div className="relative z-10 w-full space-y-1.5 rounded-sm bg-white px-4 py-3 shadow-[0_2px_14px_rgba(0,0,0,0.10)] ring-1 ring-primary/[0.06] dark:bg-card">
                    {/* Letterhead + letter date */}
                    <div className="flex items-center justify-between border-b border-primary/10 pb-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="h-3.5 w-3.5 rounded-sm bg-primary" />
                        <span className="h-1.5 w-12 rounded-full bg-primary/20" />
                      </div>
                      <FieldInline delay={0.15}>
                        {LETTER_FIELDS.date}
                      </FieldInline>
                    </div>

                    {/* Recipient */}
                    <div className="space-y-1">
                      <FieldText delay={0.3}>{LETTER_RECIPIENT.name}</FieldText>
                      <div className="h-1.5 w-2/5 rounded-full bg-primary/10" />
                    </div>

                    {/* Salutation */}
                    <p className="text-[8.5px] text-foreground/70">
                      Dear Sir / Madam,
                    </p>

                    {/* Company line (mirrors the letter editor's company format) */}
                    <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 leading-snug">
                      <FieldInline delay={0.4} mono={false}>
                        {COMPANY.name}
                      </FieldInline>
                      <FieldInline delay={0.48} mono={false}>
                        {COMPANY.status}
                      </FieldInline>
                      <span className="text-[8.5px] font-bold text-foreground">
                        ACN
                      </span>
                      <FieldInline delay={0.56}>{COMPANY.acn}</FieldInline>
                      <span className="text-[8.5px] font-bold text-foreground">
                        ABN
                      </span>
                      <FieldInline delay={0.64}>{COMPANY.abn}</FieldInline>
                      <span className="text-[8.5px] text-foreground/70">
                        (&ldquo;the Company&rdquo;)
                      </span>
                    </div>

                    {/* Key labelled fields — make it obviously a secured-creditor notice */}
                    <div className="space-y-1.5 rounded-sm bg-primary/[0.03] px-2 py-1.5">
                      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                        <span className="text-[8.5px] font-bold leading-snug text-foreground">
                          Giving of Notice Identifier(s):
                        </span>
                        <FieldInline delay={0.78} tone="pink">
                          {LETTER_FIELDS.identifier}
                        </FieldInline>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                        <span className="text-[8.5px] font-bold leading-snug text-foreground">
                          PPSR Registration No:
                        </span>
                        <FieldInline delay={0.9} tone="pink">
                          {LETTER_FIELDS.ppsrRegistration}
                        </FieldInline>
                      </div>
                    </div>

                    {/* Body paragraph (stylised) */}
                    <div className="space-y-1">
                      <div className="h-1.5 w-full rounded-full bg-primary/10" />
                      <div className="h-1.5 w-11/12 rounded-full bg-primary/10" />
                      <div className="h-1.5 w-4/5 rounded-full bg-primary/10" />
                    </div>

                    {/* Sign-off (stylised) */}
                    <div className="space-y-1 pt-0.5">
                      <div className="h-1.5 w-14 rounded-full bg-primary/10" />
                      <div className="h-2 w-20 rounded-full bg-primary/15" />
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}

          {/* Animated cursor for the Send gesture */}
          {cursor.visible && (
            <motion.div
              className="pointer-events-none absolute z-40 drop-shadow"
              initial={false}
              animate={{ left: `${cursor.x}%`, top: `${cursor.y}px` }}
              transition={{ duration: 0.55, ease: "easeInOut" }}
            >
              <MousePointer2 className="h-4 w-4 fill-white text-foreground" />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

/** An auto-populated text field (e.g. recipient name) with a highlight fade. */
function FieldText({
  delay,
  children,
}: {
  delay: number;
  children: React.ReactNode;
}) {
  return (
    <motion.span
      initial={{ opacity: 0, backgroundColor: "hsl(var(--primary) / 0.16)" }}
      animate={{ opacity: 1, backgroundColor: "hsl(var(--primary) / 0)" }}
      transition={{ delay, duration: 0.7 }}
      className="block truncate rounded-sm text-[9px] font-semibold text-primary"
    >
      {children}
    </motion.span>
  );
}

/**
 * An inline auto-populated token. `tone` mirrors the real letter editor's
 * field-source colours: `primary` (green) for org/job fields, `pink`
 * (fuchsia) for the "Contact Job Security Interest" source fields.
 */
function FieldInline({
  delay,
  tone = "primary",
  mono = true,
  children,
}: {
  delay: number;
  tone?: "primary" | "pink";
  /** Codes (ACN/ABN/PPSR) render monospace; names/statuses do not. */
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.4, ease: "easeOut" }}
      className={cn(
        "rounded-sm px-1 py-px text-[8px] font-semibold ring-1 ring-inset",
        mono && "font-mono",
        tone === "pink"
          ? "bg-fuchsia-100 text-fuchsia-700 ring-fuchsia-300"
          : "bg-primary/10 text-primary ring-primary/15",
      )}
    >
      {children}
    </motion.span>
  );
}
